// ── Parameter status evaluation ────────────────────────────────────────────
// Critical threshold: >20% beyond range span in either direction

// Subcategorías cuyo resultado es texto libre (sin rangos de referencia)
export const TEXT_PARAM_CATS = ['Morfología'];

// Un parámetro es descriptivo si está marcado como texto o pertenece a una
// subcategoría descriptiva (ej. Morfología).
export const isTextParam = (p) =>
  p?.valueType === 'text' || TEXT_PARAM_CATS.includes(p?.category);

// Un parámetro es binario cuando su referencia es Positivo/Negativo
export const isBinaryParam = (p) =>
  p?.valueType === 'binary' || p?.refType === 'binary';

// parseFloat tolerante: acepta coma decimal, conserva el 0 y descarta no numéricos
export const parseRefBound = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().replace(',', '.');
  if (s === '') return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

// Devuelve el texto a mostrar como referencia según el tipo configurado
export const getRefDisplay = (ref, refType) => {
  if (refType === 'binary') return 'Positivo / Negativo';
  if (refType === 'none' || !ref) return '—';
  if (refType === 'ranges') {
    const f = (rng) => (rng && (rng.min != null || rng.max != null)
      ? `${rng.min ?? ''} – ${rng.max ?? ''}`
      : '—');
    return `B: ${f(ref?.low)} / N: ${f(ref?.normal)} / A: ${f(ref?.high)}`;
  }
  if (ref && (ref.min != null || ref.max != null)) {
    return `${ref.min ?? ''} – ${ref.max ?? ''}`;
  }
  return '—';
};

// Evalúa el estado de un parámetro según su tipo de referencia configurado.
// ref es el objeto de referencia para la especie (ej: {min, max} o {low, normal, high}).
export function evaluateParameter(value, ref, refType) {
  if (refType === 'binary') {
    const s = String(value || '').trim().toLowerCase();
    if (['positivo', 'positive', '+'].includes(s)) return 'positive';
    if (['negativo', 'negative', '-'].includes(s)) return 'negative';
    return 'indeterminate';
  }

  if (refType === 'none' || !ref) return 'unknown';

  const v = parseRefBound(value);
  if (v === null) return 'indeterminate';

  if (refType === 'ranges') {
    const lowMax = parseRefBound(ref?.low?.max);
    const highMin = parseRefBound(ref?.high?.min);
    const normalMin = parseRefBound(ref?.normal?.min);
    const normalMax = parseRefBound(ref?.normal?.max);

    if (lowMax !== null && v <= lowMax) return 'low';
    if (highMin !== null && v >= highMin) return 'high';
    if (normalMin !== null && normalMax !== null && v >= normalMin && v <= normalMax) return 'normal';
    return 'unknown';
  }

  // minmax
  const mn = parseRefBound(ref?.min);
  const mx = parseRefBound(ref?.max);
  if (mn === null || mx === null) return 'unknown';

  if (v < mn) return 'low';
  if (v > mx) return 'high';
  return 'normal';
}

export const STATUS = {
  normal:        { label: 'NORMAL',     color: '#2CB8A0', bg: '#E8F8F5', symbol: '●' },
  high:          { label: 'ALTO ▲',    color: '#E53935', bg: '#FFEBEE', symbol: '▲' },
  low:           { label: 'BAJO ▼',    color: '#1565C0', bg: '#E3F2FD', symbol: '▼' },
  critical:      { label: 'ALTO ▲',    color: '#1565C0', bg: '#E3F2FD', symbol: '▲' },
  unknown:       { label: 'SIN REF.',  color: '#757575', bg: '#F5F5F5', symbol: '–' },
  indeterminate: { label: 'N/D',        color: '#757575', bg: '#F5F5F5', symbol: '–' },
  positive:      { label: 'POSITIVO +', color: '#C62828', bg: '#FCEBEA', symbol: '+' },
  negative:      { label: 'NEGATIVO −', color: '#16A34A', bg: '#DCFCE7', symbol: '−' },
};
